// src/context/AuthContext.jsx

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RESTRICTED_ROLE, RESTRICTED_ROUTES } from '../config/permissions';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (e) {
        console.error('Failed to parse userData:', e);
        localStorage.removeItem('userData');
      }
    }
    setIsLoading(false);
  }, []);

  const isRestricted = useCallback(() => {
    return user?.role === RESTRICTED_ROLE;
  }, [user?.role]);

  const canWrite = useCallback(() => {
    return user?.role !== RESTRICTED_ROLE;
  }, [user?.role]);

  // FIXED: Case-insensitive route check
  const canAccessRoute = useCallback((path) => {
    if (user?.role !== RESTRICTED_ROLE) return true;
    const lowerPath = path.toLowerCase();
    return !RESTRICTED_ROUTES.some(route => lowerPath.startsWith(route.toLowerCase()));
  }, [user?.role]);

  const login = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('userData', JSON.stringify(userData));
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('userData');
    localStorage.removeItem('token');
    sessionStorage.removeItem('hasReloaded');
  }, []);

  const value = useMemo(() => ({
    user,
    role: user?.role,
    isLoading,
    isAuthenticated: !!user,
    isRestricted,
    canWrite,
    canAccessRoute,
    login,
    logout,
  }), [user, isLoading, isRestricted, canWrite, canAccessRoute, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Hide content for Role 4
export const CanWrite = ({ children, fallback = null }) => {
  const { canWrite } = useAuth();
  return canWrite() ? <>{children}</> : fallback;
};

// Allow assign / reassign for ARA (role 4) and full-write roles
export const CanAssignRequest = ({ children, fallback = null }) => {
  const { canWrite, isRestricted } = useAuth();
  return canWrite() || isRestricted() ? <>{children}</> : fallback;
};

// Allow add-contact at site level for ARA (role 4) and full-write roles
export const CanAddSiteContact = ({ children, fallback = null }) => {
  const { canWrite, isRestricted } = useAuth();
  return canWrite() || isRestricted() ? <>{children}</> : fallback;
};

// Show content ONLY for Role 4
export const RestrictedBadge = ({ children }) => {
  const { isRestricted } = useAuth();
  return isRestricted() ? <>{children}</> : null;
};

// Protect routes from Role 4
export const ProtectedRoute = ({ children }) => {
  const { isRestricted, isLoading, canAccessRoute } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;

    if (isRestricted() && !canAccessRoute(location.pathname)) {
      console.log('Access denied! Redirecting to dashboard...');
      navigate('/dashboard', { replace: true });
    }
  }, [isLoading, isRestricted, canAccessRoute, location.pathname, navigate]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isRestricted() && !canAccessRoute(location.pathname)) {
    return null;
  }

  return <>{children}</>;
};

export default AuthContext;